import { useCallback, useRef, useSyncExternalStore } from 'react';
import { AnyResourceDefinition } from '../resources/types';
import {
  ResolverState,
  toLoading,
  toEmpty,
} from '../resolvers/definition/resolverState';
import { ResolverHttpClient } from '../resolvers/definition/httpClient';
import { useKey } from './useKey';
import { ResourceClient, SuspendedState } from '../client/types';
import { getPulseClient } from '../client/client';
import {
  BaseOptions,
  normalizeResourceOptions,
} from '../resources/descriptors';
import { processStack } from '../devtools/processStack';

export {
  skipToken,
  isSkipToken,
  emptyInitialParams,
  normalizeResourceOptions,
} from '../resources/descriptors';

export type {
  SkipToken,
  BaseOptions,
  BaseDescriptor,
  BaseDescriptorWithKey,
  ResourceWatchDescriptor,
  ResourceLoadDescriptor,
} from '../resources/descriptors';

export const initial = Object.freeze(toLoading()());

export const skipped = toEmpty()();

export const usePulseClient = () => getPulseClient();

export const useParseOptions = (
  resource: AnyResourceDefinition,
  options?: BaseOptions & {
    params?: any;
  }
) => {
  const {
    params,
    skip,
    pollInterval,
    pollGroup,
    httpClient,
    passive,
    keepAliveWhenHidden,
  } = normalizeResourceOptions(options);

  const instanceKey = useKey(resource, params);

  return {
    params,
    skip,
    pollInterval,
    pollGroup,
    httpClient,
    passive,
    keepAliveWhenHidden,
    instanceKey,
  };
};

export type SelectOption<TData, TSelected> = {
  select: (data: TData) => TSelected;
};

export const evictStaleSelectKeys = (
  cache: Map<string, unknown>,
  descriptors: {
    resource: { identifyInstance: (params: unknown) => string };
    params?: unknown;
    skip: boolean;
    select?: (data: any) => unknown;
  }[]
) => {
  const currentKeys = new Set<string>();
  for (const d of descriptors) {
    if (d.select && !d.skip) {
      currentKeys.add(d.resource.identifyInstance(d.params));
    }
  }
  for (const key of cache.keys()) {
    if (!currentKeys.has(key)) {
      cache.delete(key);
    }
  }
};

export type SelectorCache = {
  source: ResolverState<any, any>;
  selector: (data: any) => unknown;
  result: ResolverState<any, any>;
};

export function applySelector(
  state: SuspendedState<any, any, any>,
  selector: (data: any) => unknown,
  cached: SelectorCache | undefined
): SuspendedState<any, any, any>;
export function applySelector(
  state: ResolverState<any, any>,
  selector: (data: any) => unknown,
  cached: SelectorCache | undefined
): ResolverState<any, any>;
export function applySelector(
  state: ResolverState<any, any>,
  selector: (data: any) => unknown,
  cached: SelectorCache | undefined
): ResolverState<any, any> {
  if (cached && cached.source === state && cached.selector === selector) {
    return cached.result;
  }

  if (state.data === undefined) {
    return state;
  }

  const selectedData = selector(state.data);

  if (cached && Object.is(cached.result.data, selectedData)) {
    return cached.result;
  }

  return { ...state, data: selectedData } as ResolverState<any, any>;
}

export const useSyncResource = <TSnapshot>(
  getSnapshot: () => TSnapshot,
  instanceKey: string,
  resource: AnyResourceDefinition,
  params: any,
  skip: boolean,
  pollInterval?: number,
  client?: ResourceClient,
  httpClient?: ResolverHttpClient,
  pollGroup?: string,
  passive?: boolean,
  keepAliveWhenHidden?: boolean
) => {
  const stackRef = useRef<string | null>(null);
  // eslint-disable-next-line react-hooks/refs
  if (stackRef.current === null && client?.devtoolsId) {
    // eslint-disable-next-line react-hooks/refs
    stackRef.current = processStack(new Error()) ?? '';
  }

  const subscribe = useCallback(
    (callback: () => void) => {
      if (skip || !client) {
        return () => {};
      }

      return client.watch(
        {
          resource,
          params,
          instanceKey,
          pollInterval,
          httpClient,
          pollGroup,
          passive,
          keepAliveWhenHidden,
        },
        callback,
        { kind: 'external', stack: stackRef.current ?? undefined }
      );
    },
    // instanceKey is a proxy for params and resource type changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      client,
      instanceKey,
      pollInterval,
      pollGroup,
      skip,
      passive,
      keepAliveWhenHidden,
    ]
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
